import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import React, { useEffect, useMemo, useState } from "react";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { SelectOption } from "@/lib/molecules/select";
import { Card, CardContent, CardHeader } from "@/lib/shadcn/ui/card";
import { PeriodName } from "@/models";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import { overViewAdrFormatter, overViewIncomeFormatter } from "@/utils/formatters";
import { ChartConfig } from "@/lib/shadcn/ui/chart";
import { DonutChartData, DonutChartWithText } from "@/lib/shadcn/components/donut-chart-with-text";
import { SinglePointMultiBarChartData, SinglePontMultiBarChart } from "@/lib/shadcn/components/single-point-multi-bar-chart";
import { Apartment, BltStatsItem } from "@/models/responses";
import { useActiveApartmentEntities, useActiveApartmentOptions } from "@/store/slices/apartments";
import { useGetBltStatsQuery } from "@/store/booking-management-api";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import type { StatFilter } from "@/hooks/useAggregatedStats";

interface BltStats {
    grossIncome: number;
    netIncome: number;
    bookingsReceived: number;
    daysBooked: number;
    netAdr: number;
    grossAdr: number;
    leadDaysGroup: string;
}

function constructStats(
    bltStats: BltStatsItem[],
    apartmentId: number,
    groupId: number,
    entities: Record<string, Apartment>,
    apartmentPredicates: StatFilter[],
): Map<string, BltStats> {
    const stats = new Map<string, BltStats>();
    bltStats
        .filter((s) => {
            const apartment = entities[s.apartmentId];
            // Apply shared apartment filter predicates first: any apartment
            // that fails any predicate is excluded from aggregation. When the
            // apartment isn't resolvable (missing entity) but predicates are
            // active, exclude — consistent with how `apartmentDetailsMap` is
            // used in `overview.tsx`.
            if (apartmentPredicates.length > 0) {
                if (!apartment) {
                    return false;
                }
                for (const predicate of apartmentPredicates) {
                    if (!predicate(apartment)) {
                        return false;
                    }
                }
            }
            if (apartmentId === 0 && groupId === 0) {
                return true;
            } else if (apartmentId === 0) {
                return groupId === apartment?.groupId;
            } else {
                return apartmentId === apartment?.id;
            }
        })
        .forEach((s) => {
            if (!stats.has(s.leadDaysGroup)) {
                stats.set(s.leadDaysGroup, {
                    grossIncome: 0,
                    netIncome: 0,
                    bookingsReceived: 0,
                    daysBooked: 0,
                    netAdr: 0,
                    grossAdr: 0,
                    leadDaysGroup: s.leadDaysGroup,
                });
            }
            const stat = stats.get(s.leadDaysGroup);
            if (stat !== undefined) {
                stat.grossIncome += s.grossIncome;
                stat.netIncome += s.netIncome;
                stat.bookingsReceived += s.bookingsReceived;
                stat.daysBooked += s.daysBooked;
                if (s.daysBooked > 0) {
                    stat.netAdr = stat.netIncome / stat.daysBooked;
                    stat.grossAdr = stat.grossIncome / stat.daysBooked;
                }
            }
        });
    return stats;
}

const BltPage: React.FC = () => {
    const { setSelectedYear, setSelectedPeriod } = useUserSettingsDispatch();
    const { groupOptions, selectedGroup, selectedApartment, selectedPeriod, selectedYear, periodOptions, yearOptions } = useAppData();

    const apartmentEntities = useActiveApartmentEntities();
    const apartmentOptions = useActiveApartmentOptions();

    const { setSelectedGroup, setSelectedApartment } = useUserSettingsDispatch();
    const [apartmentOpts, setApartmentOpts] = useState<SelectOption[]>([]);

    const { predicates: apartmentFilterPredicates } = useApartmentFilters();

    const {
        data: bltStats,
        isLoading,
        error,
    } = useGetBltStatsQuery({
        period: selectedPeriod,
        year: selectedYear.toString(),
    });

    const { data: bltStatsPrevious } = useGetBltStatsQuery({
        period: selectedPeriod,
        year: (selectedYear - 1).toString(),
    });

    const filteredStats = useMemo(() => {
        if (!bltStats) {
            return new Map<string, BltStats>();
        }
        return constructStats(bltStats, selectedApartment, selectedGroup, apartmentEntities, apartmentFilterPredicates);
    }, [bltStats, selectedApartment, selectedGroup, apartmentEntities, apartmentFilterPredicates]);

    const filteredStatsPrevious = useMemo(() => {
        if (!bltStatsPrevious) {
            return new Map<string, BltStats>();
        }
        return constructStats(bltStatsPrevious, selectedApartment, selectedGroup, apartmentEntities, apartmentFilterPredicates);
    }, [bltStatsPrevious, selectedApartment, selectedGroup, apartmentEntities, apartmentFilterPredicates]);

    // Memoize chart data to prevent new array references on every render
    const grossIncomeData = useMemo(() => {
        const ret: DonutChartData[] = [];
        filteredStats.forEach((stat) => {
            if (stat.grossIncome !== 0) {
                ret.push({
                    group: `${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                    value: stat.grossIncome,
                    fill: `var(--color-${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                });
            }
        });
        return ret;
    }, [filteredStats]);

    const grossIncomePreviousData = useMemo(() => {
        const ret: DonutChartData[] = [];
        filteredStatsPrevious.forEach((stat) => {
            if (stat.grossIncome !== 0) {
                ret.push({
                    group: `${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                    value: stat.grossIncome,
                    fill: `var(--color-${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                });
            }
        });
        return ret;
    }, [filteredStatsPrevious]);

    const daysBookedData = useMemo(() => {
        const ret: DonutChartData[] = [];
        filteredStats.forEach((stat) => {
            if (stat.daysBooked !== 0) {
                ret.push({
                    group: `${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                    value: stat.daysBooked,
                    fill: `var(--color-${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                });
            }
        });
        return ret;
    }, [filteredStats]);

    const daysBookedPreviousData = useMemo(() => {
        const ret: DonutChartData[] = [];
        filteredStatsPrevious.forEach((stat) => {
            if (stat.daysBooked !== 0) {
                ret.push({
                    group: `${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                    value: stat.daysBooked,
                    fill: `var(--color-${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                });
            }
        });
        return ret;
    }, [filteredStatsPrevious]);

    const netIncomeData = useMemo(() => {
        const ret: DonutChartData[] = [];
        filteredStats.forEach((stat) => {
            if (stat.netIncome !== 0) {
                ret.push({
                    group: `${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                    value: stat.netIncome,
                    fill: `var(--color-${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                });
            }
        });
        return ret;
    }, [filteredStats]);

    const netIncomePreviousData = useMemo(() => {
        const ret: DonutChartData[] = [];
        filteredStatsPrevious.forEach((stat) => {
            if (stat.netIncome !== 0) {
                ret.push({
                    group: `${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                    value: stat.netIncome,
                    fill: `var(--color-${stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")}`,
                });
            }
        });
        return ret;
    }, [filteredStatsPrevious]);

    const grossAdrData = useMemo(() => {
        const ret: SinglePointMultiBarChartData = {};
        filteredStats.forEach((stat) => {
            if (stat.grossAdr !== 0) {
                ret[stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")] = stat.grossAdr;
            }
        });
        return ret;
    }, [filteredStats]);

    const grossAdrPreviousData = useMemo(() => {
        const ret: SinglePointMultiBarChartData = {};
        filteredStatsPrevious.forEach((stat) => {
            if (stat.grossAdr !== 0) {
                ret[stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")] = stat.grossAdr;
            }
        });
        return ret;
    }, [filteredStatsPrevious]);

    const netAdrData = useMemo(() => {
        const ret: SinglePointMultiBarChartData = {};
        filteredStats.forEach((stat) => {
            if (stat.netAdr !== 0) {
                ret[stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")] = stat.netAdr;
            }
        });
        return ret;
    }, [filteredStats]);

    const netAdrPreviousData = useMemo(() => {
        const ret: SinglePointMultiBarChartData = {};
        filteredStatsPrevious.forEach((stat) => {
            if (stat.netAdr !== 0) {
                ret[stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")] = stat.netAdr;
            }
        });
        return ret;
    }, [filteredStatsPrevious]);

    useEffect(() => {
        if (apartmentOptions.length === 0) {
            return;
        }
        if (selectedGroup !== 0) {
            const opts = apartmentOptions.filter((a: SelectOption) => apartmentEntities[Number(a.value)]?.groupId === Number(selectedGroup));
            setApartmentOpts([{ label: "All", value: "0" }, ...opts]);
            const isApartmentInGroup = opts.map((a) => a.value).includes(selectedApartment.toString());
            if (!isApartmentInGroup) {
                setSelectedApartment(0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroup, apartmentOptions, apartmentEntities]);

    const chartConfig = useMemo(() => {
        let chartConfig: ChartConfig = {
            grossIncome: {
                label: "Gross Income",
            },
            netIncome: {
                label: "Net Income",
            },
            bookingsReceived: {
                label: "Bookings Received",
            },
            daysBooked: {
                label: "Days Booked",
            },
            netAdr: {
                label: "Net ADR",
            },
            grossAdr: {
                label: "Gross ADR",
            },
        };
        let index = 0;
        filteredStats.forEach((stat) => {
            chartConfig[stat.leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "")] = {
                label: stat.leadDaysGroup,
                color: "hsl(var(--chart-" + ++index + "))",
            };
        });
        return chartConfig;
    }, [filteredStats]);

    return (
        <div data-testid="blt-page">
            {isLoading ? (
                <div className="flex justify-center items-center min-h-[200px]">
                    <Loader2 className="max-h-8 max-w-8 animate-spin" />
                </div>
            ) : error ? (
                <Alert variant="destructive">
                    <AlertDescription>{error.toString()}</AlertDescription>
                </Alert>
            ) : (
                <>
                    <h2 className={"font-semibold text-xl"}>Booking Lead Time Breakdown</h2>
                    <div className="flex flex-col lg:flex-row justify-between gap-2 mt-6">
                        <ApartmentFilterBar />
                        <div className="flex flex-col lg:flex-row lg:justify-end gap-2">
                            <SearchableSelect
                                variant="volt"
                                onChange={(val) => setSelectedPeriod(val as PeriodName)}
                                placeholder="Select Period"
                                options={periodOptions || []}
                                selectedValue={selectedPeriod}
                                width="w-auto"
                            />
                            <SearchableSelect
                                variant="volt"
                                onChange={(year) => setSelectedYear(Number(year))}
                                placeholder="Select Year"
                                options={yearOptions || []}
                                selectedValue={selectedYear.toString()}
                                width="w-auto"
                            />
                            <SearchableSelect
                                variant="volt"
                                selectedValue={selectedGroup.toString()}
                                onChange={(value) => {
                                    setSelectedGroup(Number(value));
                                    if (Number(value) === 0) {
                                        setApartmentOpts(apartmentOptions);
                                    } else {
                                        const opts = apartmentOptions.filter(
                                            (a: SelectOption) => apartmentEntities[Number(a.value)]?.groupId === Number(value),
                                        );
                                        setApartmentOpts([{ label: "All", value: "0" }, ...opts]);
                                    }
                                }}
                                placeholder="Select Group"
                                options={groupOptions}
                                selectedValuePrefix="Group"
                                width="w-auto"
                            />
                            <div data-testid="blt-apartment-select">
                                <SearchableSelect
                                    variant="volt"
                                    selectedValue={selectedApartment.toString()}
                                    onChange={(value) => setSelectedApartment(Number(value))}
                                    placeholder="Select Apartment"
                                    options={apartmentOpts.length == 0 ? apartmentOptions : apartmentOpts}
                                    selectedValuePrefix="Apartment"
                                    width="w-auto"
                                />
                            </div>
                        </div>
                    </div>
                    <Card className="shadow-sm mt-6">
                        <CardHeader className="overflow-visible">
                            <CardContent className="p-6">
                                <div className="grid grid-cols-3 gap-4 w-full">
                                    <div data-testid="blt-gross-income-total" className="min-w-0">
                                        <DonutChartWithText
                                            data={grossIncomeData}
                                            dataPrevious={grossIncomePreviousData}
                                            config={chartConfig}
                                            metricLabel={"Gross Income"}
                                            title={"Gross Income Breakdown"}
                                            description={`Gross Income grouped by lead days`}
                                            valueFormatter={overViewIncomeFormatter}
                                        />
                                    </div>

                                    <div className="min-w-0">
                                        <DonutChartWithText
                                            data={daysBookedData}
                                            dataPrevious={daysBookedPreviousData}
                                            config={chartConfig}
                                            metricLabel={"Days Booked"}
                                            title={"Days Booked Breakdown"}
                                            description={`Days Booked grouped by lead days`}
                                        />
                                    </div>

                                    <div className="min-w-0">
                                        <DonutChartWithText
                                            data={netIncomeData}
                                            dataPrevious={netIncomePreviousData}
                                            config={chartConfig}
                                            metricLabel={"Net Income"}
                                            title={"Net Income Breakdown"}
                                            description={`Net Income grouped by lead days`}
                                            valueFormatter={overViewIncomeFormatter}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-between space-x-4 w-full mt-6">
                                    <SinglePontMultiBarChart
                                        description={`Gross ADR grouped by lead days`}
                                        title={"Gross Adr"}
                                        data={grossAdrData}
                                        dataPrevious={grossAdrPreviousData}
                                        config={chartConfig}
                                        tickLabel={`${selectedPeriod} ${selectedYear}`}
                                        barValueFormatter={overViewAdrFormatter}
                                    />
                                    <SinglePontMultiBarChart
                                        description={`Net ADR grouped by lead days`}
                                        title={"Net Adr"}
                                        data={netAdrData}
                                        dataPrevious={netAdrPreviousData}
                                        config={chartConfig}
                                        tickLabel={`${selectedPeriod} ${selectedYear}`}
                                        barValueFormatter={overViewAdrFormatter}
                                    />
                                </div>
                            </CardContent>
                        </CardHeader>
                    </Card>
                </>
            )}
        </div>
    );
};

export default BltPage;
